import React from 'react';
import toastr from 'react-hot-toast';
import { useQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import {
  FilterParameters,
  generateFilterExpression,
  generateIncludeQuery,
  generateSortQuery,
  generateURL,
} from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import {
  ResourceShare,
  ResourceShareResponse,
} from 'types/Share/ResourceShares';
import { DropdownOption } from 'types/common/DropdownOption';

const getResourceShares = (
  resourceID: string,
  resourceTypeID: string | undefined
) => {
  if (!resourceTypeID) {
    return Promise.reject('resourceTypeID is missing');
  }
  const filters: FilterParameters[] = [
    {
      type: 'equal',
      key: 'resourceID',
      value: resourceID,
    },
    {
      type: 'equal',
      key: 'resourceTypeID',
      value: resourceTypeID,
    },
  ];

  const queryArray = [
    generateFilterExpression('and', filters),
    generateSortQuery('createdAt', 'Decs'),
    generateIncludeQuery(['applicationUser']),
  ];
  const url = generateURL(SPARK_RESOURCE_TYPES.RESOURCE_SHARES, queryArray);

  return fetchWrapper<ResourceShareResponse>({
    url: url,
    method: 'GET',
  });
};

export function useResourceShares(resourceID: string, resourceTypeID?: string) {
  const queryResult = useQuery(
    ['resourceShares', resourceID, resourceTypeID],
    () => getResourceShares(resourceID, resourceTypeID),
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`Failed to get Resource Shares: ${error.message}`);
        }
      },
     
      enabled: Boolean(resourceTypeID && resourceID),
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  const { data } = queryResult;
  const { data: resourceShares, included: applicationUsers } = data || {};

  const sortedResourceShares = resourceShares;

  const dropdownOptions: DropdownOption[] = [];

  const idMap = React.useMemo(() => {
    return sortedResourceShares?.reduce<Record<string, ResourceShare>>(
      (acc, curr) => {
        return { ...acc, [curr.id]: curr };
      },
      {}
    );
  }, [sortedResourceShares]);

  const getResourceShareUser = (userID: String | undefined) => {
    return applicationUsers?.find((user) => user.id === userID);
  };

  return {
    dropdownOptions,
    resourceShareIdMap: idMap,
    resourceShares: sortedResourceShares,
    getResourceShareUser,
    ...queryResult,
  };
}
